// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Does the regulator's effort show up in the injury figures?
//
// The map here is a SUPPORTING view, deliberately not the hero. The finest
// geography in this entire corpus is state/territory, and "jurisdiction" is not
// "state" — Comcare, Seacare and the Australian Government are schemes whose
// workers are spread across every state. Eight polygons cannot carry a site.

import 'leaflet/dist/leaflet.css';
import { openDrawer } from '../drawer';
import { barChart, sparkline } from './charts';
import { linear } from './layout';
import { card, chartBox, legend, note, segmented, viewHead } from './ui';
import { gloss } from '../glossary';
import { esc, money, num, pct, rate, tipAttr } from '../format';
import { ENFORCE, FATAL, JURISDICTION_NAMES, PHYSICAL, countColor } from '../colors';
import type { Bundle, JurSeries, Val } from '../types';

export const title = 'Enforcement';

const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

let mapMode: 'incidence' | 'inspectors' | 'fatalityRate' | 'fines' = 'incidence';

/**
 * The view re-renders on every mode change and every navigation, and each
 * render builds a fresh Leaflet map on a fresh container. Without tearing the
 * previous one down, its tile requests, its DOM listeners and its
 * ResizeObserver all survive — four mode clicks leave four live maps behind.
 */
let liveMap: { remove(): void } | null = null;
let liveObserver: ResizeObserver | null = null;

function teardownMap(): void {
  try { liveObserver?.disconnect(); } catch { /* already gone */ }
  liveObserver = null;
  try { liveMap?.remove(); } catch { /* already gone */ }
  liveMap = null;
}

interface Mode { key: keyof Bundle['enforcement']; label: string; fmt: (v: Val) => string; blurb: string; invert?: boolean }

const MODES: Record<string, Mode> = {
  incidence: { key: 'seriousIncidence', label: 'Serious claims per 1,000 employees', fmt: (v) => rate(v, 1), blurb: 'How often workers are seriously injured, adjusted for the size of the workforce.' },
  inspectors: { key: 'inspectorsPer10k', label: 'Field inspectors per 10,000 employees', fmt: (v) => rate(v, 2), blurb: 'How much inspection capacity the regulator actually fields.', invert: true },
  fatalityRate: { key: 'fatalityRateExRoad', label: 'Work deaths per 100,000 workers', fmt: (v) => rate(v, 1), blurb: 'Deaths from incidents not on a public road, by the state where the death occurred.' },
  fines: { key: 'fines', label: 'Court-ordered fines', fmt: (v) => (v === null ? '—' : `$${(v / 1e6).toFixed(2)}m`), blurb: 'Total fines ordered by courts for work health and safety offences.' },
};

function latest(s: JurSeries | undefined): number {
  if (!s) return 0;
  for (let i = s.periods.length - 1; i >= 0; i--) if (s.rows.some((r) => r.v[i] !== null)) return i;
  return s.periods.length - 1;
}

/**
 * Read a jurisdiction's value, allowing for the fact that the source is not
 * consistent about how it names one. Every indicator carries a plain "ACT" row
 * except the premium tables, which publish "ACTPrivate" — so without this the
 * ACT premium renders as a withheld dash even though the figure is published.
 */
const ALIASES: Record<string, string[]> = { ACT: ['ACT', 'ACTPrivate', 'ACTGov'] };

function valueFor(s: JurSeries | undefined, j: string, i: number): Val {
  if (!s) return null;
  for (const key of ALIASES[j] ?? [j]) {
    const row = s.rows.find((r) => r.j === key);
    if (row && row.v[i] !== null && row.v[i] !== undefined) return row.v[i];
  }
  return null;
}

/** Which row a jurisdiction's value actually came from, for the tooltip. */
function sourceRowFor(s: JurSeries | undefined, j: string, i: number): string {
  if (!s) return j;
  for (const key of ALIASES[j] ?? [j]) {
    const row = s.rows.find((r) => r.j === key);
    if (row && row.v[i] !== null && row.v[i] !== undefined) return key;
  }
  return j;
}

export function render(b: Bundle): string {
  const e = b.enforcement;
  const mode = MODES[mapMode];
  const series = e[mode.key] as JurSeries;
  const i = latest(series);

  const stateValues = STATES.map((j) => ({ j, v: valueFor(series, j, i) })).filter((d) => d.v !== null);
  const max = Math.max(...stateValues.map((d) => d.v!), 1);
  const min = Math.min(...stateValues.map((d) => d.v!));

  const incIdx = latest(e.seriousIncidence);
  const inspIdx = latest(e.inspectorsPer10k);

  // The scorecard: seven indicators per jurisdiction, each normalised within
  // its own row so the eight jurisdictions are comparable across measures with
  // completely different units.
  // `rate: false` marks an indicator that is a RAW VOLUME rather than a
  // size-adjusted rate. Shading those on the same worse-is-darker ramp as the
  // rates would say NSW enforces hardest and Tasmania barely at all, when the
  // only thing being measured is that NSW is thirteen times bigger. They are
  // shown as plain numbers, with the shading withheld and the reason stated.
  const INDICATORS: { key: keyof Bundle['enforcement']; label: string; fmt: (v: Val) => string; good: 'low' | 'high'; rate: boolean }[] = [
    { key: 'seriousIncidence', label: 'Serious claims per 1,000 employees', fmt: (v) => rate(v, 1), good: 'low', rate: true },
    { key: 'fatalityRateExRoad', label: 'Work deaths per 100,000 workers', fmt: (v) => rate(v, 1), good: 'low', rate: true },
    { key: 'inspectorsPer10k', label: 'Inspectors per 10,000 employees', fmt: (v) => rate(v, 2), good: 'high', rate: true },
    { key: 'premiumRate', label: 'Average premium, % of payroll', fmt: (v) => (v === null ? '—' : `${(v * 100).toFixed(2)}%`), good: 'low', rate: true },
    { key: 'disputeRate', label: 'Claims disputed', fmt: (v) => (v === null ? '—' : `${(v * 100).toFixed(1)}%`), good: 'low', rate: true },
    { key: 'proactiveVisits', label: 'Proactive workplace visits', fmt: (v) => num(v), good: 'high', rate: false },
    { key: 'prohibitionNotices', label: 'Prohibition notices issued', fmt: (v) => num(v), good: 'high', rate: false },
    { key: 'proceedings', label: 'Legal proceedings brought', fmt: (v) => num(v), good: 'high', rate: false },
    { key: 'fines', label: 'Court-ordered fines', fmt: (v) => (v === null ? '—' : `$${(v / 1e6).toFixed(2)}m`), good: 'high', rate: false },
  ];

  const scorecardRows = INDICATORS.map((ind) => {
    const s = e[ind.key] as JurSeries;
    const idx = latest(s);
    const vals = STATES.map((j) => valueFor(s, j, idx));
    const finite = vals.filter((v): v is number => v !== null);
    const lo = Math.min(...finite);
    const hi = Math.max(...finite);
    return {
      label: ind.label,
      isRate: ind.rate,
      period: s.periods[idx],
      cells: STATES.map((j, k) => {
        const v = vals[k];
        const t = v === null || hi === lo ? 0.5 : (v - lo) / (hi - lo);
        const from = sourceRowFor(s, j, idx);
        return {
          j,
          value: v,
          display: ind.fmt(v),
          // Raw volumes get no shading at all — see the note on INDICATORS.
          color: v === null || !ind.rate ? '#ffffff' : countColor(ind.good === 'low' ? t : 1 - t),
          tip: `${JURISDICTION_NAMES[j] ?? j}\n${ind.label}: ${ind.fmt(v)}\n${s.periods[idx]}${from !== j ? `\nPublished under "${from}"` : ''}\n\n${ind.rate ? 'Shaded darker where the result is worse.' : 'A raw count, not adjusted for the size of the jurisdiction — not shaded, and not comparable across states.'}`,
        };
      }),
    };
  });

  // Effort against outcome. Not causal, and the copy says so.
  const scatterPoints = STATES.map((j) => ({
    j,
    x: valueFor(e.inspectorsPer10k, j, inspIdx),
    y: valueFor(e.seriousIncidence, j, incIdx),
  })).filter((p) => p.x !== null && p.y !== null) as { j: string; x: number; y: number }[];

  const finesIdx = latest(e.fines);
  const natlFines = valueFor(e.fines, 'AUST', finesIdx);

  return `
    ${viewHead(
      'Enforcement',
      'Is anyone actually enforcing work health and safety law?',
      `Australia has no single work health and safety regulator — each state, territory and the Commonwealth runs its own, under broadly harmonised model laws. Safe Work Australia publishes what each of them does: how many inspectors they field, how many workplaces they visit unprompted, how many notices they issue and how much courts fine offenders. This view sets that effort against the injury and death figures.`,
    )}

    <div class="ctl-row">
      ${segmented('mapmode', [
        { value: 'incidence', label: 'Injury rate' },
        { value: 'fatalityRate', label: 'Death rate' },
        { value: 'inspectors', label: 'Inspectors' },
        { value: 'fines', label: 'Fines' },
      ], mapMode)}
      <span class="ctl-note">${esc(series.periods[i])} · ${esc(mode.blurb)}</span>
    </div>

    ${card(
      mode.label,
      `By state and territory. Eight polygons is all the geography this data has — there is no regional, council or postcode breakdown anywhere in the release, which is why the map supports this view rather than leading it. Click a state for its record.`,
      `<div class="map-wrap"><div id="enf-map" class="map-canvas" role="application" aria-label="${esc(mode.label)} by state"></div></div>
       <div class="legend"><span class="legend-item">${esc(mode.fmt(min))}<span class="ramp">${[0, 0.2, 0.4, 0.6, 0.8, 1].map((t) => `<i style="background:${countColor(mode.invert ? 1 - t : t)}"></i>`).join('')}</span>${esc(mode.fmt(max))}</span></div>`,
    )}

    ${card(
      'The enforcement scorecard',
      `Five size-adjusted rates, each shaded within its own row so jurisdictions in completely different units are comparable; darker always means worse. Below them sit four raw volumes, deliberately left unshaded — NSW issues more notices than Tasmania mostly because NSW is thirteen times larger, and colouring that in would read as an enforcement league table it is not.`,
      `<div class="mx-wrap"><table class="mx">
        <thead><tr><th class="mx-corner">Indicator</th>${STATES.map((j) => `<th scope="col">${esc(j)}</th>`).join('')}</tr></thead>
        <tbody>${scorecardRows
          .map(
            (row) => `<tr${row.isRate ? '' : ' class="mx-rawrow"'}><th scope="row">${esc(row.label)}<small>${esc(row.period)}${row.isRate ? '' : ' · raw count, not size-adjusted'}</small></th>${row.cells
              .map((c) => `<td class="mx-cell" style="background:${c.color}" data-tip="${tipAttr(c.tip)}"><span>${esc(c.display)}</span></td>`)
              .join('')}</tr>`,
          )
          .join('')}</tbody></table></div>`,
    )}

    ${card(
      'Inspection capacity against injury rate',
      `Each state placed by how many ${gloss('field inspectors', 'field active inspector')} it fields per 10,000 employees and how often its workers are seriously injured. There is no clean relationship, and that is the honest finding — inspector density is set by geography and scheme design as much as by risk, and a state with a lot of remote workplaces needs more inspectors to achieve the same coverage.`,
      chartBox(effortScatter(scatterPoints, e.inspectorsPer10k.periods[inspIdx], e.seriousIncidence.periods[incIdx]), 'wide'),
    )}

    ${card(
      `Where the fines land, ${esc(e.fines.periods[finesIdx])}`,
      `Courts ordered ${money(natlFines)} in work health and safety fines nationally. Fines are lumpy — one catastrophic prosecution can dominate a small jurisdiction's year — so the ten-year sparkline matters more than the bar.`,
      chartBox(
        barChart({
          values: STATES.map((j) => valueFor(e.fines, j, finesIdx)),
          labels: STATES,
          domain: [0, Math.max(...STATES.map((j) => valueFor(e.fines, j, finesIdx) ?? 0)) * 1.15],
          width: 760,
          height: 250,
          color: () => ENFORCE,
          fmtY: (v) => `$${(v / 1e6).toFixed(0)}m`,
          tip: (k) => {
            const j = STATES[k];
            const v = valueFor(e.fines, j, finesIdx);
            const proc = valueFor(e.proceedings, j, latest(e.proceedings));
            const conv = valueFor(e.convictions, j, latest(e.convictions));
            return `${JURISDICTION_NAMES[j] ?? j}\nFines ordered: ${money(v)}\nShare of national total: ${natlFines ? pct(((v ?? 0) / natlFines) * 100, 1) : '—'}\nLegal proceedings: ${num(proc)}\nResulting in conviction or order: ${num(conv)}`;
          },
        }),
        'wide',
      ) +
        `<div class="tbl-wrap"><table class="data" style="min-width:640px">
          <thead><tr><th scope="col">Jurisdiction</th><th scope="col" class="num">Proceedings</th><th scope="col" class="num">Convictions</th><th scope="col" class="num">Fines</th><th scope="col" class="num">Prohibition notices</th><th scope="col">Fines over time</th></tr></thead>
          <tbody>${STATES.map((j) => {
            const f = e.fines.rows.find((r) => r.j === j);
            return `<tr class="clickable" data-state="${esc(j)}" tabindex="0" role="button" aria-label="${esc(`${JURISDICTION_NAMES[j] ?? j}. Open its enforcement record.`)}" data-tip="${tipAttr(`${JURISDICTION_NAMES[j] ?? j}\nFines ${money(valueFor(e.fines, j, finesIdx))} in ${e.fines.periods[finesIdx]}\nClick for the full record`)}">
              <td>${esc(JURISDICTION_NAMES[j] ?? j)}</td>
              <td class="num">${num(valueFor(e.proceedings, j, latest(e.proceedings)))}</td>
              <td class="num">${num(valueFor(e.convictions, j, latest(e.convictions)))}</td>
              <td class="num">${money(valueFor(e.fines, j, finesIdx))}</td>
              <td class="num">${num(valueFor(e.prohibitionNotices, j, latest(e.prohibitionNotices)))}</td>
              <td>${sparkline(f?.v ?? [], ENFORCE, 96, 24)}</td>
            </tr>`;
          }).join('')}</tbody></table></div>`,
    )}

    ${note(
      `<strong>A jurisdiction is a scheme, not a place.</strong> Alongside the eight states and territories there are Comcare (Australian Government employees, who work everywhere), Seacare (maritime), and in some tables a split between the ACT government and private sectors. Their figures are never added to the states' here, because doing so double-counts workers. Safe Work Australia also notes that Tasmanian public administration claim counts are overstated from May 2021 onwards because of a coding issue at the source.`,
      'warn',
    )}
  `;
}

function effortScatter(points: { j: string; x: number; y: number }[], xPeriod: string, yPeriod: string): string {
  const W = 760;
  const H = 340;
  const box = { x: 64, y: 16, w: W - 92, h: H - 62 };
  if (points.length < 2) return '<p class="empty">Not enough jurisdictions publish both measures.</p>';
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const x = linear([0, Math.max(...xs) * 1.15], [box.x, box.x + box.w]);
  const y = linear([0, Math.max(...ys) * 1.15], [box.y + box.h, box.y]);

  let out = `<svg viewBox="0 0 ${W} ${H}" class="chart-svg" role="img" aria-label="Inspector density against serious claim incidence, ${points.length} states and territories">`;
  for (let t = 0; t <= 5; t++) {
    const gx = box.x + (box.w / 5) * t;
    const gy = box.y + (box.h / 5) * t;
    out += `<line class="ax-grid" x1="${gx.toFixed(1)}" y1="${box.y}" x2="${gx.toFixed(1)}" y2="${(box.y + box.h).toFixed(1)}"/>`;
    out += `<line class="ax-grid" x1="${box.x}" y1="${gy.toFixed(1)}" x2="${(box.x + box.w).toFixed(1)}" y2="${gy.toFixed(1)}"/>`;
    out += `<text class="ax-txt" x="${gx.toFixed(1)}" y="${(box.y + box.h + 16).toFixed(1)}" text-anchor="middle">${((x.domain[1] / 5) * t).toFixed(1)}</text>`;
    out += `<text class="ax-txt" x="${(box.x - 7).toFixed(1)}" y="${(gy + 3).toFixed(1)}" text-anchor="end">${(y.domain[1] - (y.domain[1] / 5) * t).toFixed(0)}</text>`;
  }
  out += `<text class="ax-title" x="${(box.x + box.w / 2).toFixed(1)}" y="${(H - 6).toFixed(1)}" text-anchor="middle">Field inspectors per 10,000 employees (${esc(xPeriod)}) →</text>`;
  out += `<text class="ax-title" transform="translate(13 ${(box.y + box.h / 2).toFixed(1)}) rotate(-90)" text-anchor="middle">Serious claims per 1,000 employees (${esc(yPeriod)}) →</text>`;

  for (const p of points) {
    const cx = x(p.x);
    const cy = y(p.y);
    out += `<circle class="dot" cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="7" fill="${PHYSICAL}" data-tip="${tipAttr(`${JURISDICTION_NAMES[p.j] ?? p.j}\n${p.x.toFixed(2)} inspectors per 10,000 employees\n${p.y.toFixed(1)} serious claims per 1,000 employees`)}"/>`;
    out += `<text class="node-label" x="${(cx + 11).toFixed(1)}" y="${(cy + 4).toFixed(1)}">${esc(p.j)}</text>`;
  }
  return `${out}</svg>`;
}

export async function mount(root: HTMLElement, b: Bundle, rerender: () => void): Promise<void> {
  root.querySelectorAll<HTMLButtonElement>('[data-seg="mapmode"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      mapMode = (btn.dataset.value as typeof mapMode) ?? 'incidence';
      rerender();
    });
  });

  // Always tear down first: mount() runs again for a mode change and for every
  // navigation back to this view.
  teardownMap();

  // The per-jurisdiction table is the keyboard route into the same drawers.
  root.querySelectorAll<HTMLTableRowElement>('tr.clickable[data-state]').forEach((tr) => {
    const open = () => {
      const j = tr.dataset.state ?? '';
      openStateDrawer(b, j, JURISDICTION_NAMES[j] ?? j);
    };
    tr.addEventListener('click', open);
    tr.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); }
    });
  });

  const el = root.querySelector<HTMLElement>('#enf-map');
  if (!el) return;

  const L = await import('leaflet');
  // Leaflet's stylesheet is imported at module top level (see the import above).

  const e = b.enforcement;
  const mode = MODES[mapMode];
  const series = e[mode.key] as JurSeries;
  const i = latest(series);
  const vals = STATES.map((j) => valueFor(series, j, i)).filter((v): v is number => v !== null);
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);

  let geo: GeoJSON.FeatureCollection;
  try {
    const res = await fetch('data/au-states.geojson');
    if (!res.ok) throw new Error(String(res.status));
    geo = await res.json();
  } catch {
    el.innerHTML = '<p class="error">Could not load state boundaries.</p>';
    return;
  }

  // The container can be detached again while the dynamic import above is in
  // flight (a fast click through to another view), and mounting Leaflet on a
  // detached node leaves an unreachable map behind.
  if (!el.isConnected) return;

  const map = L.map(el, { scrollWheelZoom: false, attributionControl: true });
  liveMap = map;
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a> · Boundaries: ABS ASGS (CC BY 4.0)',
    maxZoom: 10,
  }).addTo(map);

  const layer = L.geoJSON(geo, {
    style: (f) => {
      const code = (f?.properties as { code?: string })?.code ?? '';
      const v = valueFor(series, code, i);
      const t = v === null || hi === lo ? 0 : (v - lo) / (hi - lo);
      return {
        fillColor: v === null ? '#e9e4dd' : countColor(mode.invert ? 1 - t : t),
        fillOpacity: 0.86,
        color: '#ffffff',
        weight: 1.2,
      };
    },
    onEachFeature: (f, lyr) => {
      const props = f.properties as { code: string; name: string };
      const v = valueFor(series, props.code, i);
      lyr.bindTooltip(
        `<b>${esc(props.name)}</b>${esc(mode.label)}: ${esc(mode.fmt(v))}<br>${esc(series.periods[i])}`,
        { className: 'map-tip', sticky: true },
      );
      lyr.on('click', () => openStateDrawer(b, props.code, props.name));
    },
  }).addTo(map);

  // Leaflet never puts a tab stop on a vector path — it only does that for
  // markers — so `lyr.on('keydown')` can never fire and the drill-down would be
  // mouse-only. Promote each rendered <path> to a real button.
  //
  // This has to run AFTER addTo(map): inside onEachFeature the layer has not
  // been rendered yet and getElement() returns undefined, so doing it there
  // silently does nothing at all.
  const makeFocusable = () => {
    layer.eachLayer((lyr) => {
      const el = (lyr as unknown as { getElement?: () => SVGElement | undefined }).getElement?.();
      const props = (lyr as unknown as { feature?: { properties: { code: string; name: string } } }).feature?.properties;
      if (!el || !props || el.hasAttribute('tabindex')) return;
      const v = valueFor(series, props.code, i);
      el.setAttribute('tabindex', '0');
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', `${props.name}. ${mode.label}: ${mode.fmt(v)}. Open its record.`);
      el.addEventListener('keydown', (ev) => {
        const ke = ev as KeyboardEvent;
        if (ke.key === 'Enter' || ke.key === ' ') { ke.preventDefault(); openStateDrawer(b, props.code, props.name); }
      });
    });
  };
  makeFocusable();
  map.whenReady(makeFocusable);

  const fit = () => {
    const bounds = layer.getBounds();
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [12, 12] });
  };
  fit();
  // A cold deep-link can land before the container has a size; retry until the
  // projection settles rather than leaving a map zoomed to the wrong place.
  map.whenReady(fit);
  setTimeout(fit, 120);
  setTimeout(fit, 400);
  if (typeof ResizeObserver !== 'undefined') {
    liveObserver = new ResizeObserver(() => { map.invalidateSize(); fit(); });
    liveObserver.observe(el);
  }
  void FATAL;
}

function openStateDrawer(b: Bundle, code: string, name: string): void {

  const e = b.enforcement;
  const row = (key: keyof Bundle['enforcement'], label: string, fmt: (v: Val) => string) => {
    const s = e[key] as JurSeries;
    const i = latest(s);
    return `<dt>${esc(label)}</dt><dd>${esc(fmt(valueFor(s, code, i)))} <small>${esc(s.periods[i])}</small></dd>`;
  };

  const fatalRow = b.fatalities.state.rows.find((r) => r.name === name);
  const fatalIdx = b.fatalities.state.periods.length - 1;

  openDrawer(
    esc(name),
    `<h3>Injury and death</h3>
     <dl class="kv">
       ${row('seriousIncidence', 'Serious claims per 1,000 employees', (v) => rate(v, 1))}
       ${row('seriousFrequency', 'Serious claims per million hours', (v) => rate(v, 1))}
       ${row('longTermIncidence', 'Long-term claims per 1,000 employees', (v) => rate(v, 2))}
       ${row('fatalityRateExRoad', 'Work deaths per 100,000 workers', (v) => rate(v, 1))}
       ${fatalRow ? `<dt>Work deaths (all incidents)</dt><dd>${num(fatalRow.v[fatalIdx])} <small>${esc(b.fatalities.state.periods[fatalIdx])}</small></dd>` : ''}
       ${row('diseaseDeaths', 'Compensated occupational-disease deaths', (v) => num(v))}
     </dl>
     <h3>What the regulator did</h3>
     <dl class="kv">
       ${row('inspectorsPer10k', 'Inspectors per 10,000 employees', (v) => rate(v, 2))}
       ${row('inspectors', 'Field-active inspectors', (v) => num(v))}
       ${row('proactiveVisits', 'Proactive workplace visits', (v) => num(v))}
       ${row('reactiveVisits', 'Reactive workplace visits', (v) => num(v))}
       ${row('improvementNotices', 'Improvement notices', (v) => num(v))}
       ${row('prohibitionNotices', 'Prohibition notices', (v) => num(v))}
       ${row('undertakings', 'Enforceable undertakings', (v) => num(v))}
       ${row('proceedings', 'Legal proceedings', (v) => num(v))}
       ${row('convictions', 'Convictions, orders or agreements', (v) => num(v))}
       ${row('fines', 'Court-ordered fines', (v) => money(v))}
     </dl>
     <h3>The scheme</h3>
     <dl class="kv">
       ${row('premiumRate', 'Average premium, % of payroll', (v) => (v === null ? '—' : `${(v * 100).toFixed(2)}%`))}
       ${row('disputeRate', 'Claims disputed', (v) => (v === null ? '—' : `${(v * 100).toFixed(1)}%`))}
       ${row('selfInsuredShare', 'Claims under self-insurance', (v) => (v === null ? '—' : `${(v * 100).toFixed(1)}%`))}
     </dl>
     <p class="drawer-note">Deaths shown here are from incidents not on a public road, by the state where the death occurred. Source: Safe Work Australia, Jurisdictional Comparison and Traumatic Injury Fatalities. CC BY 4.0.</p>`,
  );
}

export { legend };
